import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { SidePanelContextRowsPropsType } from '../../SidePanelModelsProvider';
import { SidePanelProviderContext } from '../../SidePanelModelsProvider';
import type { GridCellParams } from '@mui/x-data-grid-pro';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import type { TableColumnType, TableRowType, TableType } from '../../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { useTableManipulation } from '../../../../GridDndEditor/Block/Table/useTableManipulation';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo';
import { getSanitizedDataForTableFooter } from 'utils/getSanitizedDataForTableFooter';
import { useTableTotals } from '../../../../GridDndEditor/Block/Table/useTableTotals';

export const useTableRowTypeChange = () => {
  const { toggledTableSettingsPanel } = useContext(SidePanelProviderContext) as SidePanelContextRowsPropsType;
  const { selectedBlockIdByWrapper, selectedSectionId } = useContext(SelectionContext);
  const { getTableData } = useTableManipulation();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const getTableTotals = useTableTotals();
  const [rowType, setRowType] = useState<string>('');

  useEffect(() => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper || !selectedSectionId) return;

    const selectedModel = toggledTableSettingsPanel.tableApi.selectedModel;
    const rowId = selectedModel.row?.id;
    if (!rowId) return;

    const tableData = getTableData(selectedBlockIdByWrapper, selectedSectionId);
    if (!tableData) return;

    const currentRow = tableData.rows.find((row) => row.id === rowId);
    if (!currentRow) return;

    setRowType(currentRow.rowType);
  }, [toggledTableSettingsPanel, selectedBlockIdByWrapper, selectedSectionId, getTableData]);

  const focusIntoCell = useCallback(
    (selectedRowType: string) => {
      if (!toggledTableSettingsPanel) return;

      const selectedModel = toggledTableSettingsPanel.tableApi.selectedModel;
      const rowId = selectedModel.row.id;
      let fieldToFocus = selectedModel.field;

      if (selectedRowType === RowTypesIdentifier.FOOTER || !selectedModel.isEditable) {
        const firstEditableColumn = toggledTableSettingsPanel?.tableApi?.tableCallbackDetails?.api.getAllColumns().find((column) => {
          if (column.editable) return column.field;
        });

        fieldToFocus = (firstEditableColumn as unknown as TableColumnType)?.field;
      }
      if (rowId && fieldToFocus) {
        toggledTableSettingsPanel?.tableApi.tableCallbackDetails?.api.startCellEditMode({
          id: rowId,
          field: fieldToFocus,
        });
      }
    },
    [toggledTableSettingsPanel]
  );

  const handleRowTypeChange = useCallback(
    async (event: SelectChangeEvent) => {
      if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;
      if (!selectedSectionId) throw new Error('Section id cannot be empty');
      const selectedModel = toggledTableSettingsPanel.tableApi.selectedModel;

      const tableData = getTableData(selectedBlockIdByWrapper, selectedSectionId) as TableType;
      const { columns, rows } = tableData;
      const selectedRowType = event.target.value as string;
      const { row: selectedRow } = selectedModel as GridCellParams;

      const updateRow = (row: typeof selectedRow, newRowData: Partial<typeof selectedRow>): TableRowType => {
        return {
          ...row,
          ...newRowData,
          rowType: selectedRowType,
        };
      };

      const updatedRows = rows.map((row) => {
        if (row.id !== selectedRow.id) return row;
        const newRowData = getSanitizedDataForTableFooter(row, tableData.columns, undefined, selectedRowType);

        if (selectedRowType === RowTypesIdentifier.HEADER) {
          return updateRow(row, { ...newRowData, isRowOptional: false });
        }

        if (row.type === RowTypesIdentifier.FOOTER) {
          const sanitizedFooterData = getSanitizedDataForTableFooter(row, tableData.columns);
          const updatedFooterFieldValues = Object.keys(sanitizedFooterData).reduce((acc, key) => {
            acc[key] = key === 'id' || key === '__reorder__' ? selectedRow[key] : '';
            return acc;
          }, {});
          return updateRow(row, { ...updatedFooterFieldValues, isOptionalToggleAvailable: false });
        }

        return updateRow(row, { ...newRowData });
      });

      const isOptionalRowsNotAvailable = updatedRows.some((row) => row.isOptionalToggleAvailable);
      const updatedColumns = isOptionalRowsNotAvailable
        ? columns
        : columns.filter((column) => column.columnType !== ColumnTypeIdentifier.OPTIONAL);
      const { rows: nextRows, totalRows, metadata } = getTableTotals(updatedRows, updatedColumns, tableData);
      const newTableData: TableType = {
        ...tableData,
        columns: updatedColumns,
        rows: nextRows,
      };
      handleTableContentChanged(selectedBlockIdByWrapper, selectedSectionId, tableData, {
        ...newTableData,
        totalRows,
        metadata,
      });

      setRowType(selectedRowType);
      focusIntoCell(selectedRowType);
    },
    [toggledTableSettingsPanel, selectedBlockIdByWrapper, selectedSectionId, getTableData, focusIntoCell, handleTableContentChanged]
  );

  return {
    rowType,
    handleRowTypeChange,
  };
};
